/**
 * The one function that answers "what ingredient is this line?".
 *
 * Everything that used to guess — pantry matching, the grocery list,
 * substitutions, step chips — resolves through here, so the app can no longer
 * hold two opinions about the same string. See `ingredientVocabulary.ts` for
 * why that mattered.
 *
 * Three rules distinguish this from every matcher it replaces:
 *
 *  1. LONGEST SURFACE WINS, not first-declared. The bug that started this work
 *     was `findSubstitutions` iterating object-literal order, so "rice"
 *     (declared line 215) beat "wine" (line 367) and "Shaoxing rice wine or dry
 *     sherry" offered quinoa. Here "shaoxing rice wine" is a surface form of
 *     `rice-wine` and is 18 characters long, so it is tried first.
 *
 *  2. WORD-ANCHORED, always. Substring containment is what made "farine tout
 *     usage" resolve to sage, "gin" to ginger and "buttermilk" to butter. A
 *     surface form only matches on whole-word boundaries.
 *
 *  3. NO REVERSE CONTAINMENT. The old code also asked whether the *key*
 *     contained the line, which is how "salt" became butter (via the alias
 *     "unsalted butter") and "Sauce" became soy sauce. That direction is gone
 *     and must not come back.
 *
 * Confidence is a real output, not decoration: callers render `probably` as a
 * question rather than a silent guess, so a mid-band match asks the user
 * instead of quietly being wrong in one direction.
 */

import { parseIngredientText } from './ingredientParser';
import {
  SURFACE_FORMS,
  INGREDIENT_ENTRIES,
  idForSurface,
  entryFor,
  foldSurface,
  type CanonicalIngredientId,
} from './ingredientVocabulary';

export type ResolutionSource =
  | 'exact'
  | 'vocabulary'
  | 'heuristic'
  | 'gemini'
  | 'user'
  | 'none';

export interface IngredientRef {
  canonicalId: CanonicalIngredientId | null;
  /** What the row should call this — the matched surface, not the whole line. */
  headNoun: string;
  quantity?: number;
  unit?: string;
  /** Words the line carried that the vocabulary did not consume. */
  qualifiers: string[];
  /** "X or Y" — each is independently acceptable. Empty when the line is simple. */
  alternatives: IngredientRef[];
  /**
   * "X and Y" — the line names more than one food and you need ALL of them.
   * Distinct from `alternatives`, and the distinction is load-bearing: with
   * "Sel et poivre" (58 lines in the shipped catalog) treating pepper as an
   * alternative would say a pantry holding only pepper covers the line.
   */
  components: IngredientRef[];
  confidence: number;
  source: ResolutionSource;
  /** The text this ref was resolved from, for debugging and eval output. */
  raw: string;
}

/**
 * Words that may follow the matched food without weakening the match.
 *
 * "spinach leaves" is spinach; "lime leaves" is NOT lime, but that case never
 * reaches this list because "lime leaves" is itself a surface form and, being
 * longer, wins rule 1. This list only decides whether a *leftover* word should
 * cost confidence.
 */
const TRAILING_FORM_WORDS = new Set([
  'leaf', 'leaves', 'clove', 'cloves', 'piece', 'pieces', 'bunch', 'bunches',
  'head', 'heads', 'stalk', 'stalks', 'sprig', 'sprigs', 'wedge', 'wedges',
  'slice', 'slices', 'half', 'halves', 'quarter', 'quarters', 'stick', 'sticks',
  'fillet', 'fillets', 'breast', 'thigh', 'can', 'cans', 'jar', 'jars',
  'package', 'packages', 'container', 'containers', 'bottle', 'bottles',
  'feuille', 'feuilles', 'gousse', 'gousses', 'morceau', 'morceaux',
  'tranche', 'tranches', 'branche', 'branches', 'brin', 'brins', 'botte',
  'quartier', 'quartiers', 'boite', 'boites', 'pot', 'pots', 'filet', 'filets',
]);

/**
 * Preparation and quality words that carry no identity. Deliberately a
 * qualifier list, not a delete list: they are kept on the ref so a caller can
 * still show "coarsely chopped" next to the name.
 */
const QUALIFIER_WORDS = new Set([
  'fresh', 'freshly', 'dried', 'frozen', 'canned', 'chopped', 'diced', 'minced',
  'sliced', 'shredded', 'grated', 'crushed', 'ground', 'whole', 'halved',
  'quartered', 'cubed', 'julienned', 'thinly', 'thickly', 'finely', 'coarsely',
  'roughly', 'packed', 'loosely', 'firmly', 'lightly', 'softened', 'melted',
  'cold', 'warm', 'hot', 'cooked', 'raw', 'uncooked', 'peeled', 'seeded',
  'deseeded', 'pitted', 'boneless', 'skinless', 'trimmed', 'cleaned', 'rinsed',
  'drained', 'large', 'medium', 'small', 'extra', 'thin', 'thick', 'lean',
  'organic', 'toasted', 'roasted', 'smoked', 'unsalted', 'salted', 'sweetened',
  'unsweetened', 'plain', 'good', 'quality', 'ripe', 'optional', 'divided',
  'about', 'approximately', 'plus', 'more', 'less', 'taste', 'garnish',
  'serving', 'serve', 'needed', 'such', 'as', 'like', 'and', 'the', 'a', 'an',
  'of', 'for', 'to', 'with', 'in', 'into', 'on', 'at', 'or',
  'cut', 'pinch', 'dash', 'handful', 'splash', 'knob', 'few', 'some',
  'virgin', 'dry', 'squeezed', 'pressed', 'freshly', 'store', 'bought',
  'hard', 'soft', 'smooth', 'creamy', 'chunky', 'heirloom', 'baby', 'mini',
  // Form markers. They already did their work in `promoteForm`, so leaving them
  // to count as unknown words would penalise the very lines they corrected.
  'juice', 'jus', 'zest', 'zeste', 'peel', 'rind', 'paste', 'pate', 'puree',
  'passata', 'powder', 'poudre', 'canned', 'can', 'cans', 'tinned', 'conserve',
  // Units. `parseIngredientText` only strips a LEADING quantity, so a line that
  // opens with an adjective ("packed tablespoons dark brown sugar") keeps its
  // unit and it would otherwise read as an unknown word.
  'tablespoon', 'tablespoons', 'tbsp', 'teaspoon', 'teaspoons', 'tsp', 'cup',
  'cups', 'ounce', 'ounces', 'oz', 'pound', 'pounds', 'lb', 'lbs', 'gram',
  'grams', 'kg', 'ml', 'litre', 'liter', 'litres', 'liters', 'tasse', 'tasses',
  'cuillere', 'cuilleres', 'once', 'onces', 'livre', 'livres', 'millilitre',
  // French
  'frais', 'fraiche', 'fraiches', 'seche', 'sechee', 'seches', 'sechees',
  'congele', 'congelee', 'surgele', 'surgelee', 'hache', 'hachee', 'haches',
  'hachees', 'emince', 'emincee', 'eminces', 'emincees', 'rape', 'rapee',
  'rapes', 'rapees', 'coupe', 'coupee', 'coupes', 'coupees', 'tranche',
  'ecrase', 'ecrasee', 'moulu', 'moulue', 'entier', 'entiere', 'epepine',
  'grille', 'grillee', 'roti', 'rotie', 'sale', 'salee', 'ciselee', 'cisele',
  'finement', 'grossierement', 'legerement', 'environ', 'facultatif',
  'de', 'du', 'des', 'la', 'le', 'les', 'et', 'ou', 'a', 'au', 'aux', 'd',
  'pour', 'avec', 'en', 'dans', 'plus', 'gout', 'service', 'servir', 'petit',
  'petite', 'petits', 'petites', 'gros', 'grosse', 'bien', 'tres',
  'pincee', 'poignee', 'concentre', 'concentree', 'dilue', 'diluee', 'reduit',
  'reduite', 'non', 'nature', 'sec', 'seches', 'doux', 'douce', 'fort', 'forte',
  'jeune', 'melange', 'facultative', 'preferablement', 'idealement',
  'ancienne', 'blanchi', 'blanchie', 'moulin', 'plat', 'plate', 'presse',
  'pressee', 'deux', 'trois', 'quatre', 'demi', 'demie', 'moitie', 'tout',
  'usage', 'maison', 'tempere', 'temperee', 'degele', 'degelee',
]);

/** " or " / " ou " at a word boundary, plus the "X, or use Y" shape. */
const ALTERNATIVE_SPLIT = /\s*(?:,\s*)?\b(?:or(?:\s+use)?|ou)\b\s*/i;

/** " and " / " et " — a line naming two foods you need both of. */
const COMPONENT_SPLIT = /\s*(?:,\s*)?\b(?:and|et)\b\s*/i;

/**
 * "chopped fresh herbs, such as dill, basil, mint or parsley".
 *
 * Everything after the marker is a list of acceptable choices, not a compound
 * name. Without this the longest-surface rule picks whichever herb happens to
 * have the longest word — basil over dill — and reports it as the ingredient.
 */
const SUCH_AS = /\b(?:such as|comme|tel(?:s|le|les)? que)\b/i;

const WORD_BOUNDARY_CHARS = /[\s\-]/;

/**
 * Is `needle` present in `haystack` on whole-word boundaries?
 * Both arguments must already be folded by `foldSurface`.
 */
function containsWord(haystack: string, needle: string): number {
  let from = 0;
  for (;;) {
    const idx = haystack.indexOf(needle, from);
    if (idx === -1) return -1;
    const before = idx === 0 ? ' ' : haystack[idx - 1];
    const afterIdx = idx + needle.length;
    const after = afterIdx >= haystack.length ? ' ' : haystack[afterIdx];
    if (WORD_BOUNDARY_CHARS.test(before) && WORD_BOUNDARY_CHARS.test(after)) return idx;
    from = idx + 1;
  }
}

/** Strip parentheticals and the trailing note a recipe line often carries. */
function stripNoise(text: string): string {
  return text
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

interface SurfaceHit {
  surface: string;
  id: CanonicalIngredientId;
  index: number;
}

/**
 * Longest surface form present as a whole word. Rules 1 and 2 live here.
 *
 * With one exception: a specific food beats a category word even when the
 * category word is longer, so "fromage feta" is feta and not cheese. Only a
 * match that does not overlap the generic one counts — "cream cheese" must stay
 * cream cheese rather than being pulled apart.
 */
function bestSurface(folded: string): SurfaceHit | null {
  let generic: SurfaceHit | null = null;

  for (const surface of SURFACE_FORMS) {
    const index = containsWord(folded, surface);
    if (index === -1) continue;
    const id = idForSurface(surface);
    if (!id) continue;

    if (!entryFor(id)?.generic) {
      // A specific hit found after a generic one still wins, provided the two
      // name different parts of the line.
      if (!generic) return { surface, id, index };
      const overlaps =
        index < generic.index + generic.surface.length &&
        generic.index < index + surface.length;
      return overlaps ? generic : { surface, id, index };
    }

    if (!generic) generic = { surface, id, index };
  }

  return generic;
}

/**
 * Words that say "this is the <form> of that food", in both languages.
 *
 * This is what the `form` / `base` pair in the vocabulary is for. "Peel from 1
 * small lemon" never contains the contiguous phrase "lemon peel", so surface
 * matching alone lands on `lemon` — the whole fruit — and the shopping decision
 * is wrong. Seeing `peel` anywhere in a line whose food has a `zest` form is
 * enough to promote it.
 */
const FORM_MARKERS: Array<[IngredientFormMarker, string[]]> = [
  ['juice', ['juice', 'jus']],
  ['zest', ['zest', 'zeste', 'peel', 'rind']],
  ['paste', ['paste', 'pate']],
  ['puree', ['puree', 'passata']],
  ['powder', ['powder', 'poudre']],
];

type IngredientFormMarker = 'juice' | 'zest' | 'paste' | 'puree' | 'powder';

/** Line says "canned" in either language. */
const CANNED_MARKER = /\b(?:canned|can|cans|tinned|boite|boites|conserve|conserves)\b/;

/**
 * Promote a base match to a derived or canned entry when the line says so.
 * Returns the original id when there is nothing to promote to.
 */
function promoteForm(id: CanonicalIngredientId, folded: string): CanonicalIngredientId {
  const entry = entryFor(id);
  if (!entry || entry.form || entry.base) return id;

  for (const [form, markers] of FORM_MARKERS) {
    if (!markers.some(marker => containsWord(folded, marker) !== -1)) continue;
    const derived = Object.values(INGREDIENT_ENTRIES).find(
      candidate => candidate.base === id && candidate.form === form
    );
    if (derived) return derived.id;
  }

  if (CANNED_MARKER.test(folded)) {
    const canned = INGREDIENT_ENTRIES[`canned-${id}`] ?? INGREDIENT_ENTRIES[`canned-${id}es`];
    if (canned) return canned.id;
  }

  return id;
}

function resolveSimple(raw: string): IngredientRef {
  const parsed = parseIngredientText(raw);
  const body = stripNoise(parsed.remainder || raw);
  const folded = foldSurface(body);

  const base: Omit<IngredientRef, 'canonicalId' | 'headNoun' | 'confidence' | 'source'> = {
    quantity: parsed.numPart ? Number(parseFloat(parsed.numPart)) || undefined : undefined,
    unit: parsed.unitPart || undefined,
    qualifiers: [],
    alternatives: [],
    components: [],
    raw,
  };

  if (!folded) {
    return { ...base, canonicalId: null, headNoun: body, confidence: 0, source: 'none' };
  }

  // Whole line IS a known surface form — the strongest answer available.
  const exactId = idForSurface(folded);
  if (exactId) {
    return {
      ...base,
      canonicalId: exactId,
      headNoun: entryFor(exactId)!.en[0],
      confidence: 1,
      source: 'exact',
    };
  }

  const rawHit = bestSurface(folded);
  if (!rawHit) {
    return { ...base, canonicalId: null, headNoun: body, confidence: 0, source: 'none' };
  }

  const promotedId = promoteForm(rawHit.id, folded);
  const hit: SurfaceHit =
    promotedId === rawHit.id ? rawHit : { ...rawHit, id: promotedId };

  // What did the vocabulary not consume? Everything left is either a known
  // qualifier (costs nothing), a trailing form word (costs nothing), or an
  // unknown word — and an unknown word is exactly the signal that we may have
  // matched a modifier rather than the food.
  const leftover = folded
    .replace(hit.surface, ' ')
    .split(/[\s\-]+/)
    .filter(Boolean);

  const qualifiers: string[] = [];
  let unknown = 0;
  for (const word of leftover) {
    if (QUALIFIER_WORDS.has(word)) {
      qualifiers.push(word);
    } else if (TRAILING_FORM_WORDS.has(word)) {
      qualifiers.push(word);
    } else if (/^\d+$/.test(word)) {
      // stray numeral left by a compound quantity
    } else if (idForSurface(word) === hit.id) {
      // A second name for the food we already matched — "chapelure panko" is
      // breadcrumbs twice, not breadcrumbs plus an unknown.
      qualifiers.push(word);
    } else if (entryFor(idForSurface(word))?.generic) {
      // The category the food belongs to, spelled out: "fromage feta" is feta,
      // and "fromage" restates it rather than adding anything unknown.
      qualifiers.push(word);
    } else {
      unknown++;
    }
  }

  // An unknown word next to the match means the line may name something more
  // specific than the vocabulary knows ("cane vinegar", "Shaoxing wine" before
  // that form was added). Report it as `probably` and let the caller ask,
  // rather than silently claiming the base food.
  const confidence = unknown === 0 ? 0.9 : unknown === 1 ? 0.65 : 0.45;

  return {
    ...base,
    canonicalId: hit.id,
    headNoun: entryFor(hit.id)!.en[0],
    qualifiers,
    confidence,
    source: 'vocabulary',
  };
}

/**
 * The bare food a compound id is a kind of: `red-onion` -> `onion`.
 * Returns null when the tail is not itself an entry.
 */
function headOf(id: CanonicalIngredientId | null): CanonicalIngredientId | null {
  if (!id || !id.includes('-')) return null;
  const head = id.slice(id.lastIndexOf('-') + 1);
  return INGREDIENT_ENTRIES[head] ? head : null;
}

/**
 * Put back the head noun the writer left out of the first alternative.
 *
 * "Beef or chicken broth" means beef BROTH or chicken broth — English elides the
 * repeated head. Read literally it says the cow itself, which is how the line
 * resolved to `beef` and put a steak on the shopping list. When an alternative's
 * id is `<something>-<head>` and `<primary>-<head>` is also a real entry, the
 * elided reading is the right one.
 */
function restoreElidedHead(primary: IngredientRef, alternatives: IngredientRef[]): IngredientRef {
  // Only a bare id can be missing a head; a compound already carries one.
  if (!primary.canonicalId || primary.canonicalId.includes('-')) return primary;

  for (const alternative of alternatives) {
    const id = alternative.canonicalId;
    if (!id) continue;
    const head = id.slice(id.lastIndexOf('-') + 1);
    if (head === id) continue;
    const combined = INGREDIENT_ENTRIES[`${primary.canonicalId}-${head}`];
    if (combined) {
      return { ...primary, canonicalId: combined.id, headNoun: combined.en[0], confidence: 0.65 };
    }
  }
  return primary;
}

/**
 * Resolve one ingredient line.
 *
 * "X or Y" yields a ref for X with Y in `alternatives`; both are acceptable and
 * a caller holding either one is covered. Nothing in the app split on "or"
 * before, which is why "Beef or chicken broth" resolved to `chicken` and
 * "Shaoxing rice wine or dry sherry" resolved to `rice`.
 */
export function resolveIngredient(text: string): IngredientRef {
  const raw = (text || '').trim();
  if (!raw) {
    return {
      canonicalId: null, headNoun: '', qualifiers: [], alternatives: [],
      components: [], confidence: 0, source: 'none', raw,
    };
  }

  // Resolve the whole line first. When the line is itself a known surface form
  // ("red wine vinegar", "beurre d'arachide") no split may run — splitting
  // "and"/"et" out of a name would take the line apart at a word that belongs
  // to it.
  const whole = resolveSimple(raw);
  if (whole.source === 'exact') return whole;

  // "X, such as A, B or C" — the tail is a list of choices. Resolve it as one,
  // before the generic "or" split, because commas separate the members here and
  // nowhere else.
  //
  // Tested against the line with parentheticals removed: "cane vinegar (such as
  // Datu Puti) or unseasoned rice vinegar" names a brand, not a list of choices,
  // and reading it as one throws away the real alternative that follows.
  const suchAs = stripNoise(raw).split(SUCH_AS);
  if (suchAs.length > 1) {
    const choices = suchAs[1]
      .split(/\s*(?:,|\bor\b|\bou\b)\s*/i)
      .map(part => part.trim())
      .filter(Boolean)
      .map(part => resolveSimple(part))
      .filter(ref => ref.canonicalId !== null);
    if (choices.length > 0) {
      const [first, ...rest] = choices;
      return { ...first, alternatives: rest, confidence: Math.min(first.confidence, 0.65) };
    }
  }

  // Split before parsing the parts: the quantity belongs to the first one, and
  // "2 tbsp Shaoxing rice wine or dry sherry" must not read "dry sherry" as
  // carrying its own amount.
  const orParts = raw.split(ALTERNATIVE_SPLIT).map(p => p.trim()).filter(Boolean);
  if (orParts.length > 1) {
    let primary = resolveIngredient(orParts[0]);
    let alternatives = orParts
      .slice(1)
      .map(part => resolveIngredient(part))
      .filter(ref => ref.canonicalId !== null);

    // "thinly sliced white or red onion, or scallions" splits with an opening
    // fragment that names no food on its own — the head was elided out of it,
    // exactly as in "beef or chicken broth". Rebuild it from the alternative
    // that does name one before falling back to promotion.
    if (!primary.canonicalId && alternatives.length > 0) {
      const rebuilt = headOf(alternatives[0].canonicalId);
      if (rebuilt) {
        primary = { ...primary, canonicalId: rebuilt, headNoun: entryFor(rebuilt)!.en[0], confidence: 0.65, source: 'vocabulary' };
      } else {
        [primary, ...alternatives] = [alternatives[0], ...alternatives.slice(1)];
      }
    }

    if (alternatives.length > 0) {
      return { ...restoreElidedHead(primary, alternatives), alternatives };
    }
    // A split that resolved nothing extra was not a real alternative
    // ("salt and pepper, or to taste"). Fall through to the whole line.
  }

  const andParts = raw.split(COMPONENT_SPLIT).map(p => p.trim()).filter(Boolean);
  if (andParts.length > 1) {
    const resolved = andParts.map(part => resolveSimple(part));
    const named = resolved.filter(ref => ref.canonicalId !== null);
    // Two DIFFERENT foods named on one line. One food plus noise is just the
    // line ("garlic, minced and peeled"), and is left to the whole-line answer.
    const distinct = new Set(named.map(ref => ref.canonicalId));
    if (named.length > 1 && distinct.size > 1) {
      const [primary, ...rest] = named;
      return { ...primary, components: rest };
    }
  }

  return whole;
}

/** Every id a line accepts — the primary plus its alternatives. */
export function acceptableIds(ref: IngredientRef): CanonicalIngredientId[] {
  const ids = ref.canonicalId ? [ref.canonicalId] : [];
  for (const alt of ref.alternatives) if (alt.canonicalId) ids.push(alt.canonicalId);
  return ids;
}

/** Every id a line REQUIRES — the primary plus its components. */
export function requiredIds(ref: IngredientRef): CanonicalIngredientId[] {
  const ids = ref.canonicalId ? [ref.canonicalId] : [];
  for (const part of ref.components) if (part.canonicalId) ids.push(part.canonicalId);
  return ids;
}
